import {
  IDockviewPanelProps,
} from "dockview-react";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "./utils/api";

interface INewObservation {
  description: string;
  content: string;
  date: string;
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "indent",
  "link",
  "image",
];

export function ObservationEditor({
  id,
  initialDescription = "",
  initialContent = "",
  initialDate = "",
  onClose,
}: {
  id?: number;
  initialDescription?: string;
  initialContent?: string;
  initialDate?: string;
  onClose: () => void;
}) {
  const [observation, setObservation] = useState<INewObservation>({
    description: initialDescription,
    content: initialContent,
    date: initialDate,
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  async function submit() {
    if (
      observation.description.trim() === "" ||
      observation.content.trim() === ""
    ) {
      setErrorMessage("Description and content are required");
      setSuccessMessage("");
      return;
    }
    if (observation.date && !dateRegex.test(observation.date)) {
      setErrorMessage("Date must be in YYYY-MM-DD format");
      setSuccessMessage("");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      if (id) {
        await api.put(`/observation/${id}`, { id, ...observation });
        setSuccessMessage("Observation updated successfully");
      } else {
        await api.post<INewObservation>("/observation", observation);
        setSuccessMessage("Observation added successfully");
        setObservation({ description: "", content: "", date: "" });
      }
    } catch (error: any) {
      console.error(error?.message ?? error);
      setErrorMessage("Failed to add observation");
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <div>Loading…</div>;
  }

  return (
    <div className="flex flex-col h-full w-full">
      <form
        className="flex flex-col flex-1 min-h-0"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="p-4 flex-shrink-0">
          <div className="flex justify-between gap-2 mb-4">
            <button
              type="button"
              className="btn btn-default"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
            >
              Close
            </button>
            <button type="submit" className="btn btn-default">
              Submit
            </button>
          </div>

          {errorMessage && <div className="text-red-500">{errorMessage}</div>}
          {successMessage && <div className="text-green-500">{successMessage}</div>}

          {id ? <h1>Edit observation #{id}</h1> : <h1>Add new observation</h1>}

          <div className="form-group w-32">
            <label htmlFor="date">Date:</label>
            <input
              type="text"
              id="date"
              placeholder="YYYY-MM-DD"
              value={observation.date}
              onChange={(e) =>
                setObservation((prev) => ({ ...prev, date: e.target.value }))
              }
              className="p-1 block w-full"
            />
          </div>
          <div className="form-group">
            <label htmlFor="desc">Description:</label>
            <textarea
              rows={2}
              id="desc"
              value={observation.description}
              onChange={(e) => {
                setObservation((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
              className="p-1 block w-full"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 px-4 pb-4">
          <ReactQuill
            className="quill-fill-container h-full"
            theme="snow"
            value={observation.content}
            onChange={(content) => {
              setObservation((prev) => ({ ...prev, content }));
            }}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
      </form>
    </div>
  );
}

export function LibraryEditor(props: IDockviewPanelProps) {
  const id = props.params.id ? Number(props.params.id) : undefined;

  return (
    <ObservationEditor
      id={id}
      initialDescription={props.params.description ?? ""}
      initialContent={props.params.content ?? ""}
      onClose={() => props.api.close()}
    />
  );
}
